import { AlertCircle, ArrowRight, CheckCircle2, GitBranch, Link2, Loader2, Network, RadioTower, Search, X } from "lucide-react";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import type { GraphLink, NoteCard, RelationType, SourceNode } from "../types";
import { defaultStatusForKind } from "../lib/lifecycle";
import { findRelationTargets } from "../lib/api";

const ObsidianForceGraph = lazy(() => import("./ObsidianForceGraph"));

type Props = {
  sources: SourceNode[];
  notes: NoteCard[];
  links: GraphLink[];
  selectedSource?: SourceNode;
  relationTypes: RelationType[];
  relationError?: string;
  onOpenInbox: () => void;
  onCreateRelation: (input: { sourceObjectId: string; relationType: string; targetObjectId: string }) => Promise<void>;
};

type ConnectTarget = {
  id: string;
  title: string;
  kind: string;
  group: "same-source" | "note" | "source";
  context: string;
  content?: string;
  alreadyLinked?: boolean;
  searchText: string;
};

type GraphNode = {
  id: string;
  title: string;
  label: string;
  content: string;
  status: string;
};

const INITIAL_VISIBLE_COUNT = 12;
const LOAD_MORE_COUNT = 88;
const QUICK_GRAPH_NODE_LIMIT = 30;
const QUICK_GRAPH_NODE_LIMIT_PRESETS = [30, 60, 100];

const fallbackRelationTypes: RelationType[] = [
  { id: "relation_belongs_to", slug: "belongs_to", name: "Belongs to", nameVi: "Thuộc về", description: "", descriptionVi: "Note này thuộc về một nguồn hoặc ngữ cảnh lớn hơn.", directional: true, system: true },
  { id: "relation_mentions", slug: "mentions", name: "Mentions", nameVi: "Nhắc tới", description: "", descriptionVi: "Note nhắc tới một concept, người, sự kiện hoặc thực thể.", directional: true, system: true },
  { id: "relation_supports", slug: "supports", name: "Supports", nameVi: "Ủng hộ", description: "", descriptionVi: "Note này củng cố hoặc làm rõ một ý khác.", directional: true, system: true },
  { id: "relation_contradicts", slug: "contradicts", name: "Contradicts", nameVi: "Mâu thuẫn với", description: "", descriptionVi: "Note này mâu thuẫn hoặc phản biện một ý khác.", directional: false, system: true },
  { id: "relation_turns_into_task", slug: "turns_into_task", name: "Turns into task", nameVi: "Tạo việc cần làm", description: "", descriptionVi: "Note này tạo ra một việc cần làm tiếp theo.", directional: true, system: true },
  { id: "relation_enriches", slug: "enriches", name: "Enriches", nameVi: "Làm giàu", description: "", descriptionVi: "Note mới bổ sung bối cảnh hoặc làm giàu note/concept cũ.", directional: true, system: true }
];

const percent = (value: number, total: number) => {
  if (total === 0) {
    return 0;
  }

  return Math.round((value / total) * 100);
};

const cleanLabel = (label: string) => label.replaceAll("_", " ");
const relationLabel = (type: RelationType) => type.nameVi || type.name || cleanLabel(type.slug);
const relationDescription = (type?: RelationType) => type?.descriptionVi || type?.description || "";

function labelText(value?: string) {
  return value?.replace(/_/g, " ") || "object";
}

function nodeFromSource(source: SourceNode): GraphNode {
  return {
    id: source.id,
    title: source.title,
    label: labelText(source.kind),
    content: "",
    status: source.status ?? defaultStatusForKind(source.kind)
  };
}

function nodeFromNote(note: NoteCard): GraphNode {
  return {
    id: note.id,
    title: note.title,
    label: labelText(note.kind),
    content: note.content ?? "",
    status: note.status ?? defaultStatusForKind(note.kind)
  };
}

function buildDegreeMap(links: GraphLink[]) {
  const degreeById = new Map<string, number>();

  for (const link of links) {
    const fromId = link.fromId ?? link.from;
    const toId = link.toId ?? link.to;
    degreeById.set(fromId, (degreeById.get(fromId) ?? 0) + 1);
    degreeById.set(toId, (degreeById.get(toId) ?? 0) + 1);
  }

  return degreeById;
}

function buildNeighborIds(links: GraphLink[], selectedId?: string, maxDepth = 1) {
  const set = new Set<string>();
  if (!selectedId) {
    return set;
  }

  const adjacency = new Map<string, Set<string>>();
  const connect = (left: string, right: string) => {
    if (!adjacency.has(left)) {
      adjacency.set(left, new Set());
    }
    adjacency.get(left)!.add(right);
  };

  for (const link of links) {
    const fromId = link.fromId ?? link.from;
    const toId = link.toId ?? link.to;
    connect(fromId, toId);
    connect(toId, fromId);
  }

  const visited = new Set<string>([selectedId]);
  let frontier = new Set<string>([selectedId]);
  for (let depth = 0; depth < maxDepth && frontier.size > 0; depth += 1) {
    const next = new Set<string>();
    for (const id of frontier) {
      for (const neighbor of adjacency.get(id) ?? []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          next.add(neighbor);
          set.add(neighbor);
        }
      }
    }
    frontier = next;
  }

  return set;
}

export function NetworkPanel({ sources, notes, links, selectedSource, relationTypes, relationError, onOpenInbox, onCreateRelation }: Props) {
  const [activeNoteId, setActiveNoteId] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [targetId, setTargetId] = useState("");
  const [relationType, setRelationType] = useState("mentions");
  const [savingNoteId, setSavingNoteId] = useState("");
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(() => new Set());
  const [localError, setLocalError] = useState("");
  const [noteKindFilter, setNoteKindFilter] = useState("all");
  const [sourceKindFilter, setSourceKindFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [quickLinkActive, setQuickLinkActive] = useState(false);
  const [graphSourceId, setGraphSourceId] = useState("");
  const [graphTargetId, setGraphTargetId] = useState("");
  const [graphRelationType, setGraphRelationType] = useState("mentions");
  const [savingGraphRelation, setSavingGraphRelation] = useState(false);
  const [remoteTargetOptions, setRemoteTargetOptions] = useState<ConnectTarget[]>([]);
  const [targetLoading, setTargetLoading] = useState(false);
  const [quickGraphNodeLimit, setQuickGraphNodeLimit] = useState(QUICK_GRAPH_NODE_LIMIT);
  const [quickGraphScope, setQuickGraphScope] = useState<"source" | "all">("source");

  const sourceTitleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const source of sources) {
      map.set(source.id, source.title);
    }
    return map;
  }, [sources]);

  const sourcePathById = useMemo(() => {
    const sourceById = new Map(sources.map((source) => [source.id, source]));
    const pathById = new Map<string, string>();

    for (const source of sources) {
      const chain: string[] = [];
      let current: SourceNode | undefined = source;
      const seen = new Set<string>();

      while (current && !seen.has(current.id)) {
        chain.unshift(current.title);
        seen.add(current.id);
        current = current.parentId ? sourceById.get(current.parentId) : undefined;
      }

      pathById.set(source.id, chain.join(" / "));
    }

    return pathById;
  }, [sources]);

  const inboxBase = useMemo(
    () => [...notes].filter((note) => !reviewedIds.has(note.id)).filter((note) => note.relationCount <= 1),
    [notes, reviewedIds]
  );

  const noteKindOptions = useMemo(() => Array.from(new Set(inboxBase.map((note) => note.kind))).sort(), [inboxBase]);

  const sourceKindOptions = useMemo(() => {
    const sourceKindById = new Map(sources.map((source) => [source.id, source.kind]));
    return Array.from(new Set(inboxBase.map((note) => sourceKindById.get(note.sourceId)).filter((kind): kind is string => Boolean(kind)))).sort();
  }, [inboxBase, sources]);

  const inbox = useMemo(() => {
    const sourceKindById = new Map(sources.map((source) => [source.id, source.kind]));
    return inboxBase
      .filter((note) => !quickLinkActive || !selectedSource || note.sourceId === selectedSource.id)
      .filter((note) => noteKindFilter === "all" || note.kind === noteKindFilter)
      .filter((note) => sourceKindFilter === "all" || sourceKindById.get(note.sourceId) === sourceKindFilter)
      .sort((left, right) => left.relationCount - right.relationCount);
  }, [inboxBase, noteKindFilter, quickLinkActive, selectedSource, sourceKindFilter, sources]);

  const visibleInbox = useMemo(() => inbox.slice(0, visibleCount), [inbox, visibleCount]);
  const hiddenCount = Math.max(0, inbox.length - visibleInbox.length);

  useEffect(() => {
    setVisibleCount((current) => Math.min(Math.max(current, INITIAL_VISIBLE_COUNT), Math.max(inbox.length, INITIAL_VISIBLE_COUNT)));
  }, [inbox.length]);

  useEffect(() => {
    if (!selectedSource) {
      setQuickLinkActive(false);
      resetGraphConnect();
      closeConnect();
    }
  }, [selectedSource]);

  const activeNote = useMemo(() => notes.find((note) => note.id === activeNoteId), [activeNoteId, notes]);

  const relationOptions = useMemo(() => {
    const options = relationTypes.length ? relationTypes : fallbackRelationTypes;
    return options.filter((type) => type.slug !== "contains");
  }, [relationTypes]);

  const selectedRelation = relationOptions.find((type) => type.slug === relationType) ?? relationOptions[0];
  const selectedGraphRelation = relationOptions.find((type) => type.slug === graphRelationType) ?? relationOptions[0];

  const network = useMemo(() => {
    const endpointTitles = new Set(links.flatMap((link) => [link.from, link.to]));
    const sourceNoteCounts = new Map<string, number>();

    for (const note of notes) {
      sourceNoteCounts.set(note.sourceId, (sourceNoteCounts.get(note.sourceId) ?? 0) + 1);
    }

    const connectedNotes = notes.filter((note) => note.relationCount > 0 || endpointTitles.has(note.title));
    const orphanNotes = notes.filter((note) => note.relationCount === 0 && !endpointTitles.has(note.title));
    const coveredSources = sources.filter((source) => (source.noteCount || sourceNoteCounts.get(source.id) || 0) > 0);
    const uncoveredSources = sources.filter((source) => (source.noteCount || sourceNoteCounts.get(source.id) || 0) === 0);
    const strongNotes = [...notes].filter((note) => note.relationCount > 0).sort((left, right) => right.relationCount - left.relationCount).slice(0, 4);

    return {
      connectedNotes,
      orphanNotes,
      coveredSources,
      uncoveredSources,
      strongNotes,
      namedEndpoints: Array.from(endpointTitles).slice(0, 8),
      noteCoverage: percent(connectedNotes.length, notes.length),
      sourceCoverage: percent(coveredSources.length, sources.length)
    };
  }, [links, notes, sources]);

  const fallbackTargetOptions = useMemo<ConnectTarget[]>(() => {
    if (!activeNote) {
      return [];
    }

    const noteTargets = notes
      .filter((note) => note.id !== activeNote.id)
      .map((note) => {
        const context = sourcePathById.get(note.sourceId) || sourceTitleById.get(note.sourceId) || "Không rõ nguồn";
        return {
          id: note.id,
          title: note.title,
          kind: note.kind,
          group: note.sourceId === activeNote.sourceId ? ("same-source" as const) : ("note" as const),
          context,
          content: note.content,
          searchText: `${note.title} ${note.kind} ${note.content} ${context}`.toLowerCase()
        };
      });

    const sourceTargets = sources.map((source) => {
      const context = sourcePathById.get(source.id) || source.title;
      return {
        id: source.id,
        title: source.title,
        kind: source.kind,
        group: "source" as const,
        context,
        searchText: `${source.title} ${source.kind} ${context}`.toLowerCase()
      };
    });

    const query = targetSearch.trim().toLowerCase();
    return [...noteTargets, ...sourceTargets]
      .filter((target) => !query || target.searchText.includes(query))
      .sort((left, right) => {
        const rank = { "same-source": 0, note: 1, source: 2 };
        return rank[left.group] - rank[right.group] || left.title.localeCompare(right.title);
      })
      .slice(0, 10);
  }, [activeNote, notes, sourcePathById, sourceTitleById, sources, targetSearch]);

  useEffect(() => {
    if (!activeNote) {
      setRemoteTargetOptions([]);
      setTargetLoading(false);
      return;
    }

    let cancelled = false;
    setTargetLoading(true);
    findRelationTargets({ objectId: activeNote.id, q: targetSearch, limit: 10 })
      .then((payload) => {
        if (cancelled) {
          return;
        }

        setRemoteTargetOptions(
          (payload.data ?? []).map((target) => {
            const note = notes.find((item) => item.id === target.objectId);
            const group = note ? (note.sourceId === activeNote.sourceId ? "same-source" : "note") : "source";
            return {
              id: target.objectId,
              title: target.title,
              kind: target.kind,
              group,
              context: target.context,
              content: target.content,
              alreadyLinked: target.alreadyLinked,
              searchText: `${target.title} ${target.kind} ${target.content ?? ""} ${target.context}`.toLowerCase()
            };
          })
        );
      })
      .catch(() => {
        if (!cancelled) {
          setRemoteTargetOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTargetLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeNote, notes, targetSearch]);

  const targetOptions = remoteTargetOptions.length > 0 || targetLoading || targetSearch.trim() ? remoteTargetOptions : fallbackTargetOptions;

  const quickGraph = useMemo(() => {
    if (!selectedSource) {
      return { nodes: [] as GraphNode[], links: [] as GraphLink[], degreeById: new Map<string, number>() };
    }

    const childIdsByParent = new Map<string, string[]>();
    for (const source of sources) {
      if (!source.parentId) {
        continue;
      }
      childIdsByParent.set(source.parentId, [...(childIdsByParent.get(source.parentId) ?? []), source.id]);
    }

    const scopedSourceIds =
      quickGraphScope === "all"
        ? new Set(sources.map((source) => source.id))
        : (() => {
            const ids = new Set<string>();
            const queue = [selectedSource.id];
            while (queue.length > 0) {
              const sourceId = queue.shift()!;
              if (ids.has(sourceId)) {
                continue;
              }
              ids.add(sourceId);
              queue.push(...(childIdsByParent.get(sourceId) ?? []));
            }
            return ids;
          })();

    if (quickGraphScope === "all" && selectedSource) {
      scopedSourceIds.add(selectedSource.id);
    }

    const scopedSources = sources.filter((source) => scopedSourceIds.has(source.id));
    const scopedNotes = notes.filter((note) => scopedSourceIds.has(note.sourceId));
    const nodeMap = new Map<string, GraphNode>();
    for (const source of scopedSources) {
      nodeMap.set(source.id, nodeFromSource(source));
    }
    for (const note of scopedNotes) {
      nodeMap.set(note.id, nodeFromNote(note));
    }

    if (quickGraphScope === "all") {
      for (const link of links) {
        if (link.fromId && !nodeMap.has(link.fromId)) {
          nodeMap.set(link.fromId, {
            id: link.fromId,
            title: link.from,
            label: labelText(link.fromLabel),
            content: link.fromContent ?? "",
            status: link.fromStatus ?? defaultStatusForKind(link.fromLabel ?? "object")
          });
        }
        if (link.toId && !nodeMap.has(link.toId)) {
          nodeMap.set(link.toId, {
            id: link.toId,
            title: link.to,
            label: labelText(link.toLabel),
            content: link.toContent ?? "",
            status: link.toStatus ?? defaultStatusForKind(link.toLabel ?? "object")
          });
        }
      }
    }

    const scopedIds = new Set(nodeMap.keys());
    const scopedLinks = links.filter((link) => scopedIds.has(link.fromId ?? link.from) && scopedIds.has(link.toId ?? link.to));
    const linkedPairKeys = new Set(scopedLinks.map((link) => `${link.fromId ?? link.from}->${link.toId ?? link.to}`));

    const sourceContextLinks: GraphLink[] = scopedSources
      .filter((source): source is SourceNode & { parentId: string } => Boolean(source.parentId && scopedSourceIds.has(source.parentId)))
      .filter((source) => !linkedPairKeys.has(`${source.id}->${source.parentId}`) && !linkedPairKeys.has(`${source.parentId}->${source.id}`))
      .map((source) => {
        const parent = sources.find((item) => item.id === source.parentId);
        return {
          id: `quick-source-context-${source.id}`,
          fromId: source.id,
          from: source.title,
          fromLabel: source.kind,
          fromStatus: source.status,
          toId: source.parentId,
          to: parent?.title ?? "Parent source",
          toLabel: parent?.kind,
          toStatus: parent?.status,
          label: "contains",
          labelName: "contains"
        };
      });

    const noteContextLinks: GraphLink[] = scopedNotes
      .filter((note) => !linkedPairKeys.has(`${note.id}->${note.sourceId}`) && !linkedPairKeys.has(`${note.sourceId}->${note.id}`))
      .map((note) => ({
        id: `quick-context-${note.id}`,
        fromId: note.id,
        from: note.title,
        fromLabel: note.kind,
        fromContent: note.content,
        fromStatus: note.status,
        toId: note.sourceId,
        to: sourceTitleById.get(note.sourceId) ?? "Source",
        toLabel: sources.find((source) => source.id === note.sourceId)?.kind,
        toStatus: sources.find((source) => source.id === note.sourceId)?.status,
        label: "belongs_to",
        labelName: "belongs to"
      }));
    const graphLinks = [...scopedLinks, ...sourceContextLinks, ...noteContextLinks];

    return {
      nodes: Array.from(nodeMap.values()),
      links: graphLinks,
      degreeById: buildDegreeMap(graphLinks)
    };
  }, [links, notes, quickGraphScope, selectedSource, sourceTitleById, sources]);

  const graphSelectedNode = quickGraph.nodes.find((node) => node.id === (graphTargetId || graphSourceId));
  const graphNeighborIds = useMemo(() => buildNeighborIds(quickGraph.links, graphSelectedNode?.id), [graphSelectedNode?.id, quickGraph.links]);
  const visibleQuickGraphNodes = useMemo(() => {
    const sorted = [...quickGraph.nodes].sort((left, right) => (quickGraph.degreeById.get(right.id) ?? 0) - (quickGraph.degreeById.get(left.id) ?? 0));
    const top = sorted.slice(0, quickGraphNodeLimit);
    const included = new Set(top.map((node) => node.id));
    const keep = (id: string) => {
      if (id && !included.has(id)) {
        const node = quickGraph.nodes.find((item) => item.id === id);
        if (node) {
          top.push(node);
          included.add(id);
        }
      }
    };
    keep(graphSourceId);
    keep(graphTargetId);
    graphNeighborIds.forEach(keep);
    return top;
  }, [graphNeighborIds, graphSourceId, graphTargetId, quickGraph.degreeById, quickGraph.nodes, quickGraphNodeLimit]);
  const visibleQuickGraphIds = useMemo(() => new Set(visibleQuickGraphNodes.map((node) => node.id)), [visibleQuickGraphNodes]);
  const visibleQuickGraphLinks = useMemo(
    () => quickGraph.links.filter((link) => visibleQuickGraphIds.has(link.fromId ?? link.from) && visibleQuickGraphIds.has(link.toId ?? link.to)),
    [quickGraph.links, visibleQuickGraphIds]
  );
  const visibleQuickGraphDegreeById = useMemo(() => buildDegreeMap(visibleQuickGraphLinks), [visibleQuickGraphLinks]);
  const quickGraphScopeCounts = useMemo(() => {
    const childIdsByParent = new Map<string, string[]>();
    for (const source of sources) {
      if (source.parentId) {
        childIdsByParent.set(source.parentId, [...(childIdsByParent.get(source.parentId) ?? []), source.id]);
      }
    }

    const sourceIds = new Set<string>();
    if (selectedSource) {
      const queue = [selectedSource.id];
      while (queue.length > 0) {
        const sourceId = queue.shift()!;
        if (sourceIds.has(sourceId)) {
          continue;
        }
        sourceIds.add(sourceId);
        queue.push(...(childIdsByParent.get(sourceId) ?? []));
      }
    }

    const sourceScopeIds = new Set<string>(sourceIds);
    for (const note of notes) {
      if (sourceIds.has(note.sourceId)) {
        sourceScopeIds.add(note.id);
      }
    }

    const allIds = new Set<string>();
    for (const source of sources) {
      allIds.add(source.id);
    }
    for (const note of notes) {
      allIds.add(note.id);
    }
    for (const link of links) {
      allIds.add(link.fromId ?? link.from);
      allIds.add(link.toId ?? link.to);
    }

    return { source: sourceScopeIds.size, all: allIds.size };
  }, [links, notes, selectedSource, sources]);

  const selectedTarget = targetOptions.find((target) => target.id === targetId);
  const alreadyLinked = Boolean(activeNote && targetId && links.some((link) => link.fromId === activeNote.id && link.toId === targetId && link.label === relationType));
  const graphAlreadyLinked = Boolean(
    graphSourceId &&
      graphTargetId &&
      links.some((link) => link.fromId === graphSourceId && link.toId === graphTargetId && link.label === graphRelationType)
  );

  const openConnect = (note: NoteCard) => {
    setActiveNoteId(activeNoteId === note.id ? "" : note.id);
    setTargetSearch("");
    setTargetId("");
    setRelationType("mentions");
    setLocalError("");
  };

  const closeConnect = () => {
    setActiveNoteId("");
    setTargetSearch("");
    setTargetId("");
    setLocalError("");
  };

  const resetGraphConnect = () => {
    setGraphSourceId("");
    setGraphTargetId("");
    setGraphRelationType("mentions");
  };

  const skipNote = (noteId: string) => {
    setReviewedIds((current) => new Set(current).add(noteId));
    if (activeNoteId === noteId) {
      closeConnect();
    }
  };

  const changeNoteKindFilter = (value: string) => {
    setNoteKindFilter(value);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  };

  const changeSourceKindFilter = (value: string) => {
    setSourceKindFilter(value);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  };

  const toggleQuickLink = () => {
    if (!selectedSource) {
      return;
    }

    setQuickLinkActive((current) => !current);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    resetGraphConnect();
    closeConnect();
  };

  const handleGraphSelect = (id: string) => {
    if (!graphSourceId || graphSourceId === id) {
      setGraphSourceId((current) => (current === id ? "" : id));
      setGraphTargetId("");
      return;
    }

    setGraphTargetId((current) => (current === id ? "" : id));
  };

  const submitGraphRelation = async () => {
    if (!graphSourceId || !graphTargetId || graphSourceId === graphTargetId) {
      setLocalError("Chọn 2 node khác nhau trên map trước khi xác nhận nối.");
      return;
    }

    setSavingGraphRelation(true);
    setLocalError("");

    try {
      await onCreateRelation({
        sourceObjectId: graphSourceId,
        relationType: graphRelationType,
        targetObjectId: graphTargetId
      });
      resetGraphConnect();
    } finally {
      setSavingGraphRelation(false);
    }
  };

  const submitRelation = async () => {
    if (!activeNote || !targetId) {
      setLocalError("Chọn target trước khi xác nhận nối.");
      return;
    }

    setSavingNoteId(activeNote.id);
    setLocalError("");

    try {
      await onCreateRelation({
        sourceObjectId: activeNote.id,
        relationType,
        targetObjectId: targetId
      });
      setReviewedIds((current) => new Set(current).add(activeNote.id));
      closeConnect();
    } finally {
      setSavingNoteId("");
    }
  };

  const graphSourceNode = quickGraph.nodes.find((node) => node.id === graphSourceId);
  const graphTargetNode = quickGraph.nodes.find((node) => node.id === graphTargetId);
  const canSubmitGraphRelation = Boolean(graphSourceId && graphTargetId && graphSourceId !== graphTargetId && !savingGraphRelation);

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold">Network</h2>
              <p className="text-xs font-medium text-muted">
                {quickLinkActive && selectedSource ? `Nối nhanh trong: ${sourcePathById.get(selectedSource.id) ?? selectedSource.title}` : "Tổng quan các note, nguồn và liên kết đang có"}
              </p>
            </div>
            <button
              className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-bold transition-colors ${
                quickLinkActive ? "border-blue-200 bg-brand text-white hover:bg-blue-700" : "border-blue-100 bg-blue-50 text-brand hover:bg-blue-100"
              } disabled:cursor-not-allowed disabled:border-line disabled:bg-slate-50 disabled:text-muted`}
              type="button"
              disabled={!selectedSource}
              onClick={toggleQuickLink}
              title={selectedSource ? "Mở map nối nhanh riêng trong Network, không đụng tab Bản đồ" : "Chọn một source trước khi mở Nối nhanh"}
            >
              <Network size={16} aria-hidden="true" />
              {quickLinkActive ? "Đóng map nối" : "Nối nhanh"}
            </button>
          </div>
        </div>
      </section>

      {quickLinkActive && selectedSource ? (
        <section className="rounded-lg border border-blue-100 bg-white shadow-panel">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-blue-100 px-4 py-3">
            <div>
              <h2 className="text-sm font-bold">Nối nhanh bằng map</h2>
              <p className="text-xs font-medium text-muted">
                Chọn phạm vi map, click node nguồn, click node đích, chọn relation rồi xác nhận.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted">
                <span>Phạm vi:</span>
                <div className="inline-flex rounded-lg border border-line bg-white p-1">
                  <button
                    className={`inline-flex h-7 items-center rounded-md px-2.5 ${
                      quickGraphScope === "source" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    type="button"
                    onClick={() => {
                      setQuickGraphScope("source");
                      setQuickGraphNodeLimit(QUICK_GRAPH_NODE_LIMIT);
                      resetGraphConnect();
                    }}
                  >
                    Nguồn này ({quickGraphScopeCounts.source})
                  </button>
                  <button
                    className={`inline-flex h-7 items-center rounded-md px-2.5 ${
                      quickGraphScope === "all" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    type="button"
                    onClick={() => {
                      setQuickGraphScope("all");
                      setQuickGraphNodeLimit(QUICK_GRAPH_NODE_LIMIT);
                      resetGraphConnect();
                    }}
                  >
                    Toàn thư viện ({quickGraphScopeCounts.all})
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-muted">
                <span>Số node:</span>
                <div className="inline-flex rounded-lg border border-line bg-white p-1">
                  {QUICK_GRAPH_NODE_LIMIT_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      className={`inline-flex h-7 items-center rounded-md px-2.5 ${
                        quickGraphNodeLimit === preset ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                      }`}
                      type="button"
                      onClick={() => setQuickGraphNodeLimit(preset)}
                    >
                      {preset}
                    </button>
                  ))}
                  <button
                    className={`inline-flex h-7 items-center rounded-md px-2.5 ${
                      quickGraphNodeLimit >= quickGraph.nodes.length ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    type="button"
                    onClick={() => setQuickGraphNodeLimit(quickGraph.nodes.length)}
                  >
                    Tất cả ({quickGraph.nodes.length})
                  </button>
                </div>
                <span className="text-muted">· hiện {visibleQuickGraphNodes.length}</span>
              </div>
            </div>
          </div>
          <div className="grid gap-3 p-4 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-h-[440px] overflow-hidden rounded-lg border border-line bg-white">
              {quickGraph.nodes.length > 0 ? (
                <Suspense
                  fallback={
                    <div className="flex h-[440px] items-center justify-center text-sm font-bold text-muted">
                      <Loader2 className="mr-2 animate-spin" size={16} aria-hidden="true" />
                      Đang dựng map nối nhanh
                    </div>
                  }
                >
                  <ObsidianForceGraph
                    nodes={visibleQuickGraphNodes}
                    links={visibleQuickGraphLinks}
                    degreeById={visibleQuickGraphDegreeById}
                    selectedNode={graphSelectedNode}
                    neighborIds={graphNeighborIds}
                    onSelect={handleGraphSelect}
                  />
                </Suspense>
              ) : (
                <div className="flex h-[440px] items-center justify-center px-4 text-center text-sm font-bold text-muted">
                  Source này chưa có node để vẽ map.
                </div>
              )}
            </div>
            <div className="space-y-3 rounded-lg border border-line bg-slate-50 p-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-muted">Setup nối</p>
                <h3 className="mt-1 text-base font-bold text-ink">{sourcePathById.get(selectedSource.id) ?? selectedSource.title}</h3>
              </div>

              <GraphPickCard label="Node nguồn" node={graphSourceNode} empty="Click node bắt đầu trên map" />
              <div className="flex justify-center text-brand">
                <ArrowRight size={22} aria-hidden="true" />
              </div>
              <GraphPickCard label="Node đích" node={graphTargetNode} empty="Click node cần nối tới" />

              <div>
                <label className="mb-1 block text-xs font-bold text-slate-700" htmlFor="quick-map-relation">
                  Loại relation
                </label>
                <select
                  id="quick-map-relation"
                  className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700"
                  value={graphRelationType}
                  onChange={(event) => setGraphRelationType(event.target.value)}
                >
                  {relationOptions.map((option) => (
                    <option key={option.id} value={option.slug}>
                      {relationLabel(option)}
                    </option>
                  ))}
                </select>
                {selectedGraphRelation ? (
                  <p className="mt-2 rounded-md border border-line bg-white px-3 py-2 text-xs font-semibold text-slate-600">
                    {relationDescription(selectedGraphRelation) || selectedGraphRelation.slug}
                  </p>
                ) : null}
              </div>

              {graphSourceNode && graphTargetNode ? (
                <div className="rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                  <span className="font-bold text-brand">Preview:</span> {graphSourceNode.title}{" "}
                  <span className="text-muted">--{relationLabel(selectedGraphRelation)}--&gt;</span> {graphTargetNode.title}
                  {graphAlreadyLinked ? <p className="mt-1 text-focus">Relation này đã tồn tại.</p> : null}
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <button
                  className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  type="button"
                  disabled={!canSubmitGraphRelation}
                  onClick={() => void submitGraphRelation()}
                >
                  {savingGraphRelation ? <Loader2 className="animate-spin" size={14} aria-hidden="true" /> : <Link2 size={14} aria-hidden="true" />}
                  {graphAlreadyLinked ? "Đánh dấu đã nối" : "Xác nhận nối"}
                </button>
                <button
                  className="inline-flex h-9 items-center justify-center rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-600 hover:bg-slate-50"
                  type="button"
                  onClick={resetGraphConnect}
                >
                  Xóa chọn
                </button>
              </div>

              {localError || relationError ? (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700" role="alert">
                  {localError || relationError}
                </p>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div>
            <h2 className="text-sm font-bold">Cần nối</h2>
            <p className="text-xs font-medium text-muted">
              {quickLinkActive && selectedSource ? `Đang chỉ hiện note thuộc ${sourcePathById.get(selectedSource.id) ?? selectedSource.title}.` : "Review note ít liên kết rồi nối ngay tại chỗ, giống luồng backlink của Capacities."}
            </p>
          </div>
          <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full border border-orange-100 bg-orange-50 px-2 text-xs font-bold text-focus">
            {inbox.length}
          </span>
        </div>
        <div className="space-y-2 p-4">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-slate-50 p-3">
            <div className="min-w-44">
              <label className="mb-1 block text-xs font-bold text-muted" htmlFor="need-link-note-kind">
                Loại note
              </label>
              <select
                id="need-link-note-kind"
                className="h-9 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700"
                value={noteKindFilter}
                onChange={(event) => changeNoteKindFilter(event.target.value)}
              >
                <option value="all">Tất cả note</option>
                {noteKindOptions.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-44">
              <label className="mb-1 block text-xs font-bold text-muted" htmlFor="need-link-source-kind">
                Loại source
              </label>
              <select
                id="need-link-source-kind"
                className="h-9 w-full rounded-lg border border-line bg-white px-3 text-sm font-semibold text-slate-700"
                value={sourceKindFilter}
                onChange={(event) => changeSourceKindFilter(event.target.value)}
              >
                <option value="all">Tất cả source</option>
                {sourceKindOptions.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </div>
            <div className="ml-auto text-xs font-bold text-muted">
              Đang hiện {visibleInbox.length}/{inbox.length} note
            </div>
          </div>

          {inbox.length === 0 ? (
            <p className="rounded-lg border border-green-100 bg-green-50 px-3 py-2 text-xs font-bold text-leaf">Không còn note phù hợp với bộ lọc hiện tại.</p>
          ) : (
            <>
              {visibleInbox.map((note) => (
                <div key={note.id} className="rounded-lg border border-line bg-white">
                  <div className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-base font-bold leading-6 text-ink">
                        {sourcePathById.get(note.sourceId) ?? sourceTitleById.get(note.sourceId) ?? "Không rõ nguồn"}
                      </h3>
                      <p className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-sm font-normal leading-6 text-slate-600">{note.content}</p>
                    </div>
                    <span
                      className={`shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-bold ${
                        note.relationCount === 0 ? "border-orange-100 bg-orange-50 text-focus" : "border-line bg-slate-50 text-muted"
                      }`}
                      title={`${note.relationCount} liên kết`}
                    >
                      {note.relationCount === 0 ? "0 liên kết" : `${note.relationCount} liên kết`}
                    </span>
                    <button
                      className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700"
                      type="button"
                      onClick={() => openConnect(note)}
                    >
                      <Link2 size={14} aria-hidden="true" />
                      {activeNoteId === note.id ? "Đóng" : "Nối"}
                    </button>
                  </div>

                  {activeNoteId === note.id ? (
                    <div className="border-t border-blue-100 bg-blue-50/70 p-3">
                      <FullNotePreview note={note} sourcePath={sourcePathById.get(note.sourceId) ?? sourceTitleById.get(note.sourceId) ?? "Không rõ nguồn"} />

                      <div className="mt-3 grid gap-3 xl:grid-cols-[260px_40px_minmax(0,1fr)]">
                        <div className="flex flex-col justify-center gap-2">
                          <label className="text-xs font-bold text-slate-700" htmlFor={`relation-${note.id}`}>
                            Loại relation
                          </label>
                          <select
                            id={`relation-${note.id}`}
                            className="h-9 w-full rounded-lg border border-blue-200 bg-white px-3 text-xs font-bold text-slate-700"
                            value={relationType}
                            onChange={(event) => setRelationType(event.target.value)}
                          >
                            {relationOptions.map((option) => (
                              <option key={option.id} value={option.slug}>
                                {relationLabel(option)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center justify-center text-brand xl:pt-6">
                          <ArrowRight size={22} aria-hidden="true" />
                        </div>
                        <ConnectPreview
                          title={selectedTarget?.title ?? "Chọn target để nối"}
                          subtitle={selectedTarget?.context ?? "Tìm note, concept hoặc source liên quan"}
                          body={selectedTarget?.content}
                          badge={selectedTarget?.kind ?? "target"}
                          muted={!selectedTarget}
                        />
                      </div>

                      {selectedRelation ? (
                        <p className="mt-3 rounded-md border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-600">
                          {relationDescription(selectedRelation) || selectedRelation.slug}
                        </p>
                      ) : null}

                      {selectedTarget ? (
                        <div className="mt-3 rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                          <span className="font-bold text-brand">Preview:</span>{" "}
                          {sourcePathById.get(note.sourceId) ?? sourceTitleById.get(note.sourceId) ?? note.title}{" "}
                          <span className="text-muted">--{relationLabel(selectedRelation)}--&gt;</span> {selectedTarget.title}
                        </div>
                      ) : null}

                      <div className="mt-3">
                        <label className="mb-1 block text-xs font-bold text-slate-700" htmlFor={`target-search-${note.id}`}>
                          Tìm target
                        </label>
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} aria-hidden="true" />
                          <input
                            id={`target-search-${note.id}`}
                            className="h-10 w-full rounded-lg border border-blue-200 bg-white pl-9 pr-3 text-sm font-semibold text-slate-700"
                            value={targetSearch}
                            onChange={(event) => setTargetSearch(event.target.value)}
                            placeholder="Gõ tên note, source hoặc nội dung liên quan"
                          />
                        </div>
                      </div>

                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {targetOptions.map((target) => {
                          const isSelected = target.id === targetId;
                          const isDuplicate = target.alreadyLinked || links.some((link) => link.fromId === note.id && link.toId === target.id && link.label === relationType);

                          return (
                            <button
                              key={target.id}
                              className={`min-h-16 cursor-pointer rounded-lg border px-3 py-2 text-left transition-colors ${
                                isSelected ? "border-brand bg-white text-ink shadow-sm" : "border-blue-100 bg-white/80 text-slate-700 hover:border-blue-200 hover:bg-white"
                              }`}
                              type="button"
                              onClick={() => setTargetId(target.id)}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <p className="line-clamp-1 text-sm font-bold">{target.title}</p>
                                <span className="shrink-0 rounded-md border border-line bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-muted">{target.kind}</span>
                              </div>
                              <p className="mt-1 line-clamp-1 text-xs font-semibold text-muted">{target.context}</p>
                              {target.content ? <p className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-xs font-normal leading-5 text-slate-600">{target.content}</p> : null}
                              {isDuplicate ? <p className="mt-1 text-xs font-bold text-focus">Đã có relation này</p> : null}
                            </button>
                          );
                        })}
                      </div>

                      {targetLoading ? (
                        <p className="mt-3 inline-flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-xs font-bold text-muted">
                          <Loader2 className="animate-spin" size={14} aria-hidden="true" />
                          Đang tìm target
                        </p>
                      ) : targetOptions.length === 0 ? (
                        <p className="mt-3 rounded-lg border border-line bg-white px-3 py-2 text-xs font-bold text-muted">Không tìm thấy target phù hợp.</p>
                      ) : null}

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                        <button className="inline-flex h-8 items-center gap-1 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-600 hover:bg-slate-50" type="button" onClick={() => skipNote(note.id)}>
                          <X size={14} aria-hidden="true" />
                          Bỏ qua
                        </button>
                        <div className="flex flex-wrap items-center gap-2">
                          {alreadyLinked ? <span className="text-xs font-bold text-focus">Relation này đã tồn tại.</span> : null}
                          <button
                            className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                            type="button"
                            disabled={!targetId || savingNoteId === note.id}
                            onClick={() => void submitRelation()}
                          >
                            {savingNoteId === note.id ? <Loader2 className="animate-spin" size={14} aria-hidden="true" /> : <CheckCircle2 size={14} aria-hidden="true" />}
                            {alreadyLinked ? "Đánh dấu đã nối" : "Xác nhận nối"}
                          </button>
                        </div>
                      </div>
                      {localError || relationError ? (
                        <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700" role="alert">
                          {localError || relationError}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
              {hiddenCount > 0 ? (
                <button
                  className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  type="button"
                  onClick={() => setVisibleCount((current) => Math.min(current + LOAD_MORE_COUNT, inbox.length))}
                >
                  Load thêm {Math.min(LOAD_MORE_COUNT, hiddenCount)} note nữa
                </button>
              ) : null}
            </>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-sm font-bold">Việc nên làm</h2>
          <p className="text-xs font-medium text-muted">Lấp các chỗ còn thiếu để kho kiến thức chắc hơn</p>
        </div>
        <div className="space-y-3 p-4">
          <ReviewGroup icon={RadioTower} title="Nguồn chưa có note" empty="Mỗi nguồn đã có ít nhất một note." items={network.uncoveredSources.map((source) => source.title)} />
          <button
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700"
            type="button"
            onClick={onOpenInbox}
          >
            <GitBranch size={16} aria-hidden="true" />
            Sang Ghi chép
          </button>
        </div>
      </section>
    </div>
  );
}

function GraphPickCard({ label, node, empty }: { label: string; node?: GraphNode; empty: string }) {
  return (
    <div className={`rounded-lg border bg-white p-3 ${node ? "border-blue-100 shadow-sm" : "border-dashed border-line"}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-muted">{label}</p>
        <span className="shrink-0 rounded-md border border-line bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-muted">
          {node?.label ?? "node"}
        </span>
      </div>
      {node ? (
        <>
          <h3 className="mt-2 line-clamp-2 break-words text-sm font-bold leading-5 text-ink">{node.title}</h3>
          {node.content ? <p className="mt-2 line-clamp-3 whitespace-pre-wrap break-words text-xs font-normal leading-5 text-slate-600">{node.content}</p> : null}
        </>
      ) : (
        <p className="mt-2 text-sm font-semibold text-muted">{empty}</p>
      )}
    </div>
  );
}

function FullNotePreview({ note, sourcePath }: { note: NoteCard; sourcePath: string }) {
  return (
    <div className="rounded-lg border border-blue-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-brand">Mục cha / source</p>
          <h3 className="mt-1 break-words text-lg font-bold leading-7 text-ink">{sourcePath}</h3>
        </div>
        <span className="shrink-0 rounded-md border border-line bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-muted">{note.kind}</span>
      </div>
      <div className="mt-3 rounded-md border border-line bg-white px-3 py-2">
        <p className="line-clamp-3 whitespace-pre-wrap break-words text-base font-normal leading-7 text-slate-800">{note.content}</p>
      </div>
    </div>
  );
}

function ConnectPreview({ title, subtitle, body, badge, muted = false }: { title: string; subtitle: string; body?: string; badge: string; muted?: boolean }) {
  return (
    <div className={`min-h-28 rounded-lg border bg-white p-3 ${muted ? "border-dashed border-blue-200 text-muted" : "border-blue-100 text-ink shadow-sm"}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="line-clamp-2 text-sm font-bold">{title}</h3>
        <span className="shrink-0 rounded-md border border-line bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-muted">{badge}</span>
      </div>
      <p className="mt-2 line-clamp-2 text-xs font-semibold text-muted">{subtitle}</p>
      {body ? <p className="mt-2 line-clamp-3 whitespace-pre-wrap break-words text-xs font-normal leading-5 text-slate-600">{body}</p> : null}
    </div>
  );
}

function Metric({ icon: Icon, label, value, detail }: { icon: typeof Link2; label: string; value: string; detail: string }) {
  return (
    <div className="rounded-lg border border-line bg-slate-50 p-3">
      <div className="flex items-center gap-2 text-xs font-bold uppercase text-muted">
        <Icon size={14} aria-hidden="true" />
        {label}
      </div>
      <p className="mt-2 text-2xl font-bold">{value}</p>
      <p className="mt-1 text-xs font-semibold text-muted">{detail}</p>
    </div>
  );
}

function ReviewGroup({ icon: Icon, title, empty, items }: { icon: typeof AlertCircle; title: string; empty: string; items: string[] }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-muted">
        <Icon size={14} aria-hidden="true" />
        {title}
      </div>
      {items.length === 0 ? (
        <p className="rounded-lg border border-green-100 bg-green-50 px-3 py-2 text-xs font-bold text-leaf">{empty}</p>
      ) : (
        <div className="space-y-2">
          {items.slice(0, 4).map((item) => (
            <p key={item} className="truncate rounded-md border border-orange-100 bg-orange-50 px-3 py-2 text-xs font-bold text-focus">
              {item}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
